import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Trash2 } from "lucide-react";
import { STEPS } from "@/data/factFind";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/fact-find-drafts`;
const opts = { withCredentials: true };

const DraftRow = ({ d, fmtDate, onRemind, onDelete, busy }) => (
  <li data-testid={`draft-row-${d.id}`} className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 border-t border-black/10 py-4">
    <div className="flex-1 min-w-0">
      <p className="font-serif text-lg text-[#111413] truncate">{d.client_name || "Name not given yet"}</p>
      <a href={`mailto:${d.email}`} className="text-sm text-[#4A524D] break-all">{d.email}</a>
    </div>
    <div className="text-xs text-[#4A524D] md:w-56 space-y-0.5">
      <p>Reached: <span className="text-[#111413]">{STEPS[d.step]?.title || `Step ${d.step + 1}`}</span></p>
      <p>Last active: {fmtDate(d.updated_at)}</p>
      <p data-testid={`draft-reminders-${d.id}`}>
        Reminders sent: {d.reminders_sent}
        {d.last_reminder_at && ` · last ${fmtDate(d.last_reminder_at)}`}
      </p>
    </div>
    <div className="flex items-center gap-2">
      {d.unsubscribed ? (
        <span className="px-3 py-2 text-[11px] uppercase tracking-[0.15em] text-[#A78558]" data-testid={`draft-unsubscribed-${d.id}`}>
          Reminders stopped
        </span>
      ) : (
        <button
          data-testid={`draft-remind-${d.id}`}
          disabled={busy}
          onClick={() => onRemind(d.id)}
          className="inline-flex items-center gap-1.5 bg-[#0A2333] px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-[#F9F8F4] transition-opacity duration-200 disabled:opacity-50"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
          Send reminder now
        </button>
      )}
      <button
        data-testid={`draft-delete-${d.id}`}
        aria-label="Delete draft"
        onClick={() => onDelete(d.id)}
        className="p-2 text-[#A78558] hover:text-[#8A6A43]"
      >
        <Trash2 size={15} />
      </button>
    </div>
  </li>
);

export const AdminFactFindDrafts = ({ fmtDate }) => {
  const [drafts, setDrafts] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    axios.get(API, opts).then(({ data }) => setDrafts(data)).catch(() => setDrafts([]));
  }, []);

  const onRemind = async (id) => {
    setBusy(id);
    try {
      const { data } = await axios.post(`${API}/${id}/remind`, {}, opts);
      setDrafts((l) => l.map((d) => (d.id === id ? { ...d, ...data } : d)));
      toast.success("Reminder sent.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the reminder.");
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this unfinished fact find? The client's saved answers will be removed.")) return;
    try {
      await axios.delete(`${API}/${id}`, opts);
      setDrafts((l) => l.filter((d) => d.id !== id));
    } catch {
      toast.error("Couldn't delete that draft.");
    }
  };

  return (
    <section className="mt-10" data-testid="admin-fact-find-drafts">
      <h2 className="font-serif text-2xl tracking-tight text-[#111413]">Started but not finished</h2>
      <p className="mt-1 text-sm text-[#4A524D]">Automatic reminders go out every 3 days (up to 6). You can also send one now.</p>
      {drafts === null ? (
        <p className="mt-4 text-sm text-[#4A524D]">Loading…</p>
      ) : drafts.length === 0 ? (
        <p className="mt-4 text-sm text-[#4A524D]" data-testid="admin-drafts-empty">No unfinished fact finds.</p>
      ) : (
        <ul className="mt-4 border-b border-black/10">
          {drafts.map((d) => (
            <DraftRow key={d.id} d={d} fmtDate={fmtDate} onRemind={onRemind} onDelete={onDelete} busy={busy === d.id} />
          ))}
        </ul>
      )}
      <h2 className="mt-14 font-serif text-2xl tracking-tight text-[#111413]">Submitted</h2>
    </section>
  );
};
